import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import SectionLoader from "../../Loaders/SectionLoader";
import OverallScoreboardContainer from "./OverallScoreboardContainer";
import RaceweekHeader from "./RaceweekHeader";
import { isMobile, playerPoints } from "../../../Common/utils";
import useBattleMode from "../../HeadToHead/useBattleMode";
import { driver } from "../../../redux/actions";
import useLivePoints from "../../../Common/hooks/useLivePoints";
import HeadToHeadPlayerCard from "./HeadToHeadPlayerCard";
import PenaltyHeadToHead from "./PenaltyHeadToHead";
import { getTranslations } from "../../../Common/utils";

/**
 * Returns the players array for HeadToHeadPlayerCard based on final fix status
 * @param {Object} player - The player object (e.g., captains?.user, driver?.opponent)
 * @param {string} teamKey - 'user' or 'opponent'
 * @param {Object} finalFixOldPlayer - The final fix old player object
 * @param {Object} finalFixNewPlayer - The final fix new player object
 * @returns {Array} Array of players for the card
 */
const getFinalFixPlayersArray = (
  player,
  teamKey,
  finalFixOldPlayer,
  finalFixNewPlayer,
  swappedOutPlayer,
  team
) => {
  if (player?.isFinalFixNewPlayer && finalFixOldPlayer?.[teamKey]) {
    return {
      players: [finalFixOldPlayer?.[teamKey], player],
      icon: ["finalFix"],
      score: [
        playerPoints({ player: finalFixOldPlayer?.[teamKey], team }),
        playerPoints({ player, team }),
      ],
    };
  }
  if (player?.isFinalFixOldPlayer && finalFixNewPlayer?.[teamKey]) {
    return {
      players: [player, finalFixNewPlayer?.[teamKey]],
      icon: ["finalFix"],
      score: [
        playerPoints({ player, team }),
        playerPoints({ player: finalFixNewPlayer?.[teamKey], team }),
      ],
    };
  }
  if (player?.isSwappedInPlayer && swappedOutPlayer?.[teamKey]) {
    return {
      players: [swappedOutPlayer?.[teamKey], player],
      icon: player?.isExtraDRS ? ["extraDRS", "replaced"] : ["replaced"],
      score: [
        playerPoints({ player: swappedOutPlayer?.[teamKey], team }),
        playerPoints({ player, team }),
      ],
    };
  }
  if (player?.IsActive === "0") {
    return {
      players: [player],
      icon: player?.isExtraDRS ? ["extraDRS", "replaced"] : ["replaced"],
      score: [playerPoints({ player, team })],
    };
  }

  return {
    players: [player],
    icon: player?.isExtraDRS ? ["extraDRS"] : [],
    score: [playerPoints({ player, team })],
  };
};

const RaceweekHeadtoHead = ({ gamedayId, handleBack }) => {
  const leagueState = useSelector((state) => state.league);
  const dispatch = useDispatch();
  const driverState = useSelector((state) => state.driver);
  const { teamLivePointsCondition } = useLivePoints();
  const fixtureCircuitConfigState = useSelector(
    (state) => state.fixtureCircuitConfig
  );
  const fixtures = fixtureCircuitConfigState?.data?.fixtures;
  const {
    getSingleRaceH2HData,
    getSingleRaceH2HLoading,
    getSingleRaceH2HError,
  } = leagueState;

  const overallDataMembers = getSingleRaceH2HData?.overall?.members;

  // Memoize raceweekFixture to prevent infinite re-renders
  const raceweekFixture = useMemo(() => {
    const fixtureData = fixtures?.find(
      (fixture) => fixture?.GamedayId === gamedayId
    );
    return fixtureData ? { ...fixtureData, id: fixtureData?.GamedayId } : null;
  }, [fixtures, gamedayId]);

  useEffect(() => {
    const getRaceWeekDrivers = () => {
      if (!raceweekFixture) return;
      const payload = {
        gameDayId: raceweekFixture?.id,
        languageCode: "en",
        livePointsBuster: teamLivePointsCondition,
      };
      dispatch(driver.fetchRaceWeekDrivers(payload));
    };
    getRaceWeekDrivers();
  }, [raceweekFixture, dispatch, teamLivePointsCondition]);

  const overallLayoutVariant = isMobile() ? "stacked" : "inline-header";

  const currentGamedayData = useMemo(() => {
    const currentGameday = getSingleRaceH2HData?.gamedays?.find(
      (gameday) => gameday?.round_id === gamedayId
    );
    const currentGamedayMembers = currentGameday?.members?.map((member) => {
      const overallMember = overallDataMembers?.find(
        (overallMember) => overallMember?.user_guid === member?.user_guid
      );
      return {
        ...member,
        team_name: overallMember?.team_name,
        user_name: overallMember?.user_name,
      };
    });

    return {
      ...currentGameday,
      members: currentGamedayMembers,
      overall_battle_result: currentGameday?.battle_result,
      overall_battle_result_percentage:
        currentGameday?.battle_result_percentage,
    };
  }, [getSingleRaceH2HData, gamedayId, overallDataMembers]);

  // Memoize member data transformations to prevent infinite re-renders
  const { userMembers, opponentMembers } = useMemo(() => {
    const userMembersData = currentGamedayData?.members?.find(
      (member) => member?.is_current_user === 1
    );
    const opponentMembersData = currentGamedayData?.members?.find(
      (member) => member?.is_current_user === 0
    );

    // Transform member data to match useBattleMode expected format (snake_case to camelCase)
    const transformMemberData = (member) => {
      if (!member) return null;
      return {
        ...member,
        userGuid: member?.user_guid,
        teamNo: member?.team_no,
      };
    };

    return {
      userMembers: transformMemberData(userMembersData),
      opponentMembers: transformMemberData(opponentMembersData),
      overall_battle_result: currentGamedayData?.battle_result,
      overall_battle_result_percentage:
        currentGamedayData?.battle_result_percentage,
    };
  }, [currentGamedayData]);

  const {
    captains = {},
    drivers = [],
    constructors = [],
    finalFixNewPlayer = {},
    finalFixOldPlayer = {},
    swappedOutPlayer = {},
    userTeam = {},
    opponentTeam = {},
  } = useBattleMode({
    opponent: opponentMembers,
    user: userMembers,
    selectedRaceOption: raceweekFixture,
  });

  if (getSingleRaceH2HLoading) {
    return <SectionLoader />;
  }

  if (getSingleRaceH2HError) {
    return <div>Error: {getSingleRaceH2HError}</div>;
  }

  return (
    <div className="si-headToHead-raceweek">
      <RaceweekHeader
        countryName={raceweekFixture?.CountryName}
        gamedayId={raceweekFixture?.GamedayId}
        onBack={handleBack}
      />
      <OverallScoreboardContainer
        layoutConfig={{
          variant: overallLayoutVariant,
          containerClass: "narrow",
        }}
        overallResult={currentGamedayData}
      />
      <div className="si-headToHead-raceweek__drivers">
        <div className="si-h2h-player-card__row">
          <div className="si-h2h-player-card__item">
            <HeadToHeadPlayerCard
              cardType="current"
              players={
                getFinalFixPlayersArray(
                  captains?.user,
                  "user",
                  finalFixOldPlayer,
                  finalFixNewPlayer,
                  swappedOutPlayer,
                  userTeam
                )?.players
              }
              score={
                getFinalFixPlayersArray(
                  captains?.user,
                  "user",
                  finalFixOldPlayer,
                  finalFixNewPlayer,
                  swappedOutPlayer,
                  userTeam
                )?.score
              }
              shortName={captains?.user?.DriverTLA}
              fullName={captains?.user?.FUllName}
              firstName={captains?.user?.FirstName}
              lastName={captains?.user?.LastName}
              size={isMobile() ? "sm" : "lg"}
              boosterId={[
                "captain",
                ...getFinalFixPlayersArray(
                  captains?.user,
                  "user",
                  finalFixOldPlayer,
                  finalFixNewPlayer,
                  swappedOutPlayer,
                  userTeam
                )?.icon,
              ]}
            />
          </div>
          <div className="si-h2h-player-card__item">
            <HeadToHeadPlayerCard
              cardType="opponent"
              players={
                getFinalFixPlayersArray(
                  captains?.opponent,
                  "opponent",
                  finalFixOldPlayer,
                  finalFixNewPlayer,
                  swappedOutPlayer,
                  opponentTeam
                )?.players
              }
              score={
                getFinalFixPlayersArray(
                  captains?.opponent,
                  "opponent",
                  finalFixOldPlayer,
                  finalFixNewPlayer,
                  swappedOutPlayer,
                  opponentTeam
                )?.score
              }
              shortName={captains?.opponent?.DriverTLA}
              fullName={captains?.opponent?.FUllName}
              firstName={captains?.opponent?.FirstName}
              lastName={captains?.opponent?.LastName}
              size={isMobile() ? "sm" : "lg"}
              boosterId={[
                "captain",
                ...getFinalFixPlayersArray(
                  captains?.opponent,
                  "opponent",
                  finalFixOldPlayer,
                  finalFixNewPlayer,
                  swappedOutPlayer,
                  opponentTeam
                )?.icon,
              ]}
            />
          </div>
        </div>
        {drivers?.map((driver) => {
          if (
            driver?.user?.isSwappedOutPlayer ||
            driver?.opponent?.isSwappedOutPlayer
          ) {
            return null;
          }

          return (
            <div className="si-h2h-player-card__row">
              <div className="si-h2h-player-card__item">
                <HeadToHeadPlayerCard
                  cardType="current"
                  players={
                    getFinalFixPlayersArray(
                      driver?.user,
                      "user",
                      finalFixOldPlayer,
                      finalFixNewPlayer,
                      swappedOutPlayer,
                      userTeam
                    )?.players
                  }
                  score={
                    getFinalFixPlayersArray(
                      driver?.user,
                      "user",
                      finalFixOldPlayer,
                      finalFixNewPlayer,
                      swappedOutPlayer,
                      userTeam
                    )?.score
                  }
                  shortName={driver?.user?.DriverTLA}
                  fullName={driver?.user?.FUllName}
                  firstName={driver?.user?.FirstName}
                  lastName={driver?.user?.LastName}
                  size={isMobile() ? "sm" : "lg"}
                  boosterId={
                    getFinalFixPlayersArray(
                      driver?.user,
                      "user",
                      finalFixOldPlayer,
                      finalFixNewPlayer,
                      swappedOutPlayer,
                      userTeam
                    )?.icon
                  }
                />
              </div>
              <div className="si-h2h-player-card__item">
                <HeadToHeadPlayerCard
                  cardType="opponent"
                  players={
                    getFinalFixPlayersArray(
                      driver?.opponent,
                      "opponent",
                      finalFixOldPlayer,
                      finalFixNewPlayer,
                      swappedOutPlayer,
                      opponentTeam
                    )?.players
                  }
                  score={
                    getFinalFixPlayersArray(
                      driver?.opponent,
                      "opponent",
                      finalFixOldPlayer,
                      finalFixNewPlayer,
                      swappedOutPlayer,
                      opponentTeam
                    )?.score
                  }
                  shortName={driver?.opponent?.DriverTLA}
                  fullName={driver?.opponent?.FUllName}
                  firstName={driver?.opponent?.FirstName}
                  lastName={driver?.opponent?.LastName}
                  size={isMobile() ? "sm" : "lg"}
                  boosterId={
                    getFinalFixPlayersArray(
                      driver?.opponent,
                      "opponent",
                      finalFixOldPlayer,
                      finalFixNewPlayer,
                      swappedOutPlayer,
                      opponentTeam
                    )?.icon
                  }
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="si-headToHead-raceweek__constructors">
        {constructors?.map((constructor) => (
          <div className="si-h2h-player-card__row">
            <div className="si-h2h-player-card__item">
              <HeadToHeadPlayerCard
                cardType="current"
                players={[constructor?.user]}
                score={[
                  playerPoints({
                    player: constructor?.user,
                    team: userTeam,
                  }),
                ]}
                shortName={constructor?.user?.DriverTLA}
                fullName={constructor?.user?.FUllName}
                firstName={constructor?.user?.FirstName}
                lastName={constructor?.user?.LastName}
                size={isMobile() ? "sm" : "lg"}
                boosterId={[]}
                showOnlyLastName={true}
              />
            </div>
            <div className="si-h2h-player-card__item">
              <HeadToHeadPlayerCard
                cardType="opponent"
                players={[constructor?.opponent]}
                score={[
                  playerPoints({
                    player: constructor?.opponent,
                    team: opponentTeam,
                  }),
                ]}
                shortName={constructor?.opponent?.DriverTLA}
                fullName={constructor?.opponent?.FUllName}
                firstName={constructor?.opponent?.FirstName}
                lastName={constructor?.opponent?.LastName}
                size={isMobile() ? "sm" : "lg"}
                boosterId={[]}
                showOnlyLastName={true}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="si-headToHead-raceweek__penalties">
        <div className="si-h2h-player-card__row">
          <div className="si-h2h-player-card__item">
            {!!userTeam?.islateonboard && (
              <PenaltyHeadToHead
                title={getTranslations("h2h_penalty_title", "Penalty")}
                penalty={getTranslations(
                  "h2h_penalty_late_onboard",
                  "Late Onboard"
                )}
                penaltyPoints={10}
                showPenaltyPoints={false}
              />
            )}
            {!!userTeam?.inactive_driver_penality_points && (
              <PenaltyHeadToHead
                title={getTranslations("h2h_penalty_title", "Penalty")}
                penalty={getTranslations(
                  "h2h_penalty_inactive_driver_penalty",
                  "Inactive Driver"
                )}
                penaltyPoints={userTeam?.inactive_driver_penality_points}
              />
            )}
            {!!userTeam?.usersubs &&
              !!userTeam?.team_info?.subsallowed &&
              userTeam?.usersubs > userTeam?.team_info?.subsallowed && (
                <PenaltyHeadToHead
                  title={getTranslations("h2h_penalty_title", "Penalty")}
                  penalty={getTranslations(
                    "h2h_penalty_additional_transfers",
                    "Additional Transfers"
                  )}
                  penaltyPoints={
                    -1 *
                    userTeam?.extrasubscost *
                    (userTeam?.usersubs - userTeam?.team_info?.subsallowed)
                  }
                />
              )}
          </div>
          <div className="si-h2h-player-card__item">
            {!!opponentTeam?.islateonboard && (
              <PenaltyHeadToHead
                isOpponent={true}
                title={getTranslations("h2h_penalty_title", "Penalty")}
                penalty={getTranslations(
                  "h2h_penalty_late_onboard",
                  "Late Onboard"
                )}
                penaltyPoints={-10}
                showPenaltyPoints={false}
              />
            )}
            {!!opponentTeam?.inactive_driver_penality_points && (
              <PenaltyHeadToHead
                isOpponent={true}
                title={getTranslations("h2h_penalty_title", "Penalty")}
                penalty={getTranslations(
                  "h2h_penalty_inactive_driver_penalty",
                  "Inactive Driver"
                )}
                penaltyPoints={opponentTeam?.inactive_driver_penality_points}
              />
            )}
            {!!opponentTeam?.usersubs &&
              !!opponentTeam?.team_info?.subsallowed &&
              opponentTeam?.usersubs > opponentTeam?.team_info?.subsallowed && (
                <PenaltyHeadToHead
                  isOpponent={true}
                  title={getTranslations("h2h_penalty_title", "Penalty")}
                  penalty={getTranslations(
                    "h2h_penalty_additional_transfers",
                    "Additional Transfers"
                  )}
                  penaltyPoints={
                    -1 *
                    opponentTeam?.extrasubscost *
                    (opponentTeam?.usersubs -
                      opponentTeam?.team_info?.subsallowed)
                  }
                />
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RaceweekHeadtoHead;
